<section
  class="workflow-edge-editor"
  [class.workflow-edge-editor--contextual]="contextual"
  aria-labelledby="workflow-edge-editor-title"
  data-testid="workflow-draft-edge-editor"
>
  <p
    *ngIf="statusMessage() as message"
    class="workflow-edge-editor__notice"
    role="status"
    aria-live="polite"
  >
    {{ message }}
  </p>

  <div
    *ngIf="errorMessage() as message"
    #edgeErrorSummary
    class="workflow-edge-editor__error"
    data-testid="edge-editor-error"
    role="alert"
    aria-live="assertive"
    tabindex="-1"
  >
    <strong>Path could not be changed</strong>
    <p>{{ message }}</p>
  </div>

  <div class="workflow-edge-editor__workspace">
    <section
      class="workflow-edge-editor__navigator"
      [hidden]="contextual"
      aria-labelledby="workflow-edge-editor-title"
    >
      <header class="workflow-edge-editor__navigator-header">
        <div>
          <h4 #edgeSectionHeading id="workflow-edge-editor-title" tabindex="-1">
            Step paths
          </h4>
          <span aria-live="polite">
            {{ edgeRows.length }} path{{ edgeRows.length === 1 ? "" : "s" }}
          </span>
        </div>
        <button
          uiButton
          severity="primary"
          [outlined]="false"
          *ngIf="canAuthor"
          type="button"
          class="workflow-edge-editor__add-button"
          [disabled]="addButtonConfig[0].disabled"
          (click)="onAddButtonClick(addButtonConfig[0])"
        >
          Add path
        </button>
      </header>

      <p
        *ngIf="edgeRows.length === 0"
        class="workflow-edge-editor__empty"
        role="status"
      >
        No step paths are defined in this
        {{ inspectionMode ? "immutable version" : "draft" }}.
      </p>

      <nav
        *ngIf="edgeRows.length > 0"
        class="workflow-edge-editor__path-navigation"
        aria-label="Workflow step paths"
      >
        <ol class="workflow-edge-editor__path-list">
          <li *ngFor="let row of edgeRows; trackBy: trackEdgeRow">
            <button
              [wrap]="true"
              uiButton
              [text]="true"
              [outlined]="false"
              type="button"
              class="workflow-edge-editor__path-row"
              [class.workflow-edge-editor__path-row--selected]="
                !isAddingEdge() && selectedEdgeIndex() === row.index
              "
              [class.workflow-edge-editor__path-row--invalid]="!row.edge"
              [attr.aria-current]="
                !isAddingEdge() && selectedEdgeIndex() === row.index
                  ? 'true'
                  : null
              "
              [attr.aria-label]="
                row.edge
                  ? 'Path from ' + row.fromLabel + ' to ' + row.toLabel
                  : 'Invalid path entry ' + (row.index + 1)
              "
              [disabled]="interactionBlocked"
              (click)="reviewEdgeAt(row.index, $event.detail === 0)"
            >
              <span class="workflow-edge-editor__path-copy">
                <span
                  *ngIf="row.resultLabel"
                  class="workflow-edge-editor__path-result"
                  >{{ row.resultLabel }}</span
                >
                <strong>{{ row.fromLabel }}</strong>
                <span
                  *ngIf="row.edge; else invalidPathSummary"
                  class="workflow-edge-editor__path-destination"
                >
                  <span aria-hidden="true">→</span>
                  {{ row.toLabel }}
                </span>
                <ng-template #invalidPathSummary>
                  <span class="workflow-edge-editor__path-problem">
                    Path details are missing
                  </span>
                </ng-template>
              </span>
              <span
                *ngIf="row.edge; else invalidPathBadge"
                class="workflow-edge-editor__row-arrow"
                aria-hidden="true"
              >
                ›
              </span>
              <ng-template #invalidPathBadge>
                <span class="workflow-edge-editor__invalid-badge">Invalid</span>
              </ng-template>
            </button>
          </li>
        </ol>
      </nav>
    </section>

    <section
      class="workflow-edge-editor__inspector"
      data-testid="edge-editor-inspector"
      aria-labelledby="workflow-edge-form-title"
    >
      <ng-container
        *ngIf="selectedEdgeRow || isAddingEdge(); else noPathSelected"
      >
        <header class="workflow-edge-editor__inspector-header">
          <p class="workflow-edge-editor__eyebrow">
            {{
              isAddingEdge()
                ? "New map path"
                : hasMalformedSelection
                  ? inspectionMode
                    ? "Saved evidence"
                    : "Draft evidence"
                  : "Selected map path"
            }}
          </p>
          <h5 #edgeEditorHeading id="workflow-edge-form-title" tabindex="-1">
            <ng-container *ngIf="isAddingEdge()">Add a path</ng-container>
            <ng-container *ngIf="!isAddingEdge() && selectedEdgeRow as row">
              <ng-container *ngIf="row.edge; else invalidPathHeading">
                {{ row.fromLabel }}
                <span aria-hidden="true">→</span>
                {{ row.toLabel }}
              </ng-container>
              <ng-template #invalidPathHeading>
                Invalid path entry {{ row.index + 1 }}
              </ng-template>
            </ng-container>
          </h5>
          <p
            *ngIf="!isAddingEdge() && selectedEdgeRow?.resultLabel"
            class="workflow-edge-editor__selected-result"
          >
            {{ selectedEdgeRow?.resultLabel }}
          </p>
          <p class="workflow-edge-editor__scope-note">
            For workflows using saved graph execution, these paths determine
            where work goes next. Save, validate, and publish changes before
            they can affect new work.
          </p>
        </header>

        <ng-container
          *ngIf="!hasMalformedSelection; else malformedPathInspector"
        >
          <div class="workflow-edge-editor__details">
            <h6>Path details</h6>
            <p
              *ngIf="isAddingEdge()"
              class="workflow-edge-editor__new-path-guidance"
            >
              Choose where work starts and where it goes next. For branches and
              return visits, select an existing routing decision or configure
              action outcomes in Step behavior → Outcomes &amp; routing.
            </p>
            <p *ngIf="!canAuthor" class="workflow-edge-editor__hint">
              {{
                inspectionMode
                  ? "This path belongs to an immutable workflow version."
                  : "You can inspect this path. Author permission is required to change it."
              }}
            </p>
            <ui-form
              #edgeForm
              instanceId="workflow-draft-path-form"
              [attr.aria-label]="
                isAddingEdge() ? 'New workflow path' : 'Selected workflow path'
              "
              [config]="edgeFormConfig"
              [formValues]="edgeFormValues"
              [disabled]="!canAuthor || interactionBlocked"
              (formValuesChange)="onEdgeFormChange($event)"
            ></ui-form>
            <div
              class="workflow-edge-editor__routing-guidance"
              [class.workflow-edge-editor__routing-guidance--repair]="
                hasUnsupportedRoutingSettings
              "
            >
              <p>{{ pathGuidance }}</p>
              <button
                uiButton
                *ngIf="hasUnsupportedRoutingSettings && canAuthor"
                type="button"
                [disabled]="interactionBlocked"
                (click)="useOrdinaryContinuation()"
              >
                Use ordinary continuation
              </button>
            </div>
          </div>

          <details
            *ngIf="selectedEdge as edge"
            class="workflow-edge-editor__technical"
          >
            <summary>Technical identity</summary>
            <dl>
              <div>
                <dt>Stable path ID</dt>
                <dd>
                  <code>{{ edge.id }}</code>
                </dd>
              </div>
              <div>
                <dt>From node ID</dt>
                <dd>
                  <code>{{ edge.fromNodeId }}</code>
                </dd>
              </div>
              <div>
                <dt>Next node ID</dt>
                <dd>
                  <code>{{ edge.toNodeId }}</code>
                </dd>
              </div>
              <div *ngIf="edge.outcomeCode">
                <dt>Saved routing result</dt>
                <dd>
                  <code>{{ edge.outcomeCode }}</code>
                </dd>
              </div>
              <div *ngIf="edge.pathKindCode">
                <dt>Saved path behavior</dt>
                <dd>
                  <code>{{ edge.pathKindCode }}</code>
                </dd>
              </div>
            </dl>
          </details>

          <p
            *ngIf="pendingChanges()"
            class="workflow-edge-editor__pending"
            role="status"
          >
            <strong>Save draft</strong> includes these path edits. You can also
            apply them to the map before saving.
          </p>

          <div
            *ngIf="canAuthor"
            class="workflow-edge-editor__editor-actions"
            role="group"
            aria-label="Path editor actions"
          >
            <button
              uiButton
              severity="primary"
              [outlined]="false"
              type="button"
              class="workflow-edge-editor__primary-action"
              [disabled]="editorButtonConfig[0].disabled"
              (click)="onEditorButtonClick(editorButtonConfig[0])"
            >
              {{ isAddingEdge() ? "Add path" : "Apply path changes" }}
            </button>
            <button
              uiButton
              type="button"
              [disabled]="editorButtonConfig[1].disabled"
              (click)="onEditorButtonClick(editorButtonConfig[1])"
            >
              {{ isAddingEdge() ? "Cancel" : "Reset changes" }}
            </button>
            <button
              uiButton
              *ngIf="!isAddingEdge()"
              type="button"
              class="workflow-edge-editor__remove-action"
              [disabled]="editorButtonConfig[2].disabled"
              (click)="onEditorButtonClick(editorButtonConfig[2])"
            >
              Remove path
            </button>
          </div>
        </ng-container>

        <ng-template #malformedPathInspector>
          <div class="workflow-edge-editor__malformed">
            <h6>Path details are missing</h6>
            <p>
              This
              {{ inspectionMode ? "immutable definition" : "draft" }} contains
              an empty path entry. It remains here as validation evidence and
              will not be changed automatically.
            </p>
            <details class="workflow-edge-editor__technical">
              <summary>Technical identity</summary>
              <dl>
                <div>
                  <dt>Document position</dt>
                  <dd>
                    <code>edges[{{ selectedEdgeIndex() }}]</code>
                  </dd>
                </div>
                <div>
                  <dt>Stored value</dt>
                  <dd><code>null</code></dd>
                </div>
              </dl>
            </details>
            <button
              uiButton
              *ngIf="canAuthor"
              type="button"
              class="workflow-edge-editor__remove-action workflow-edge-editor__standalone-action"
              [disabled]="editorButtonConfig[2].disabled"
              (click)="onEditorButtonClick(editorButtonConfig[2])"
            >
              Remove invalid entry
            </button>
          </div>
        </ng-template>
      </ng-container>

      <ng-template #noPathSelected>
        <div class="workflow-edge-editor__no-selection">
          <h5 #edgeEditorHeading id="workflow-edge-form-title" tabindex="-1">
            Choose a step path
          </h5>
          <p>
            Select a relationship from the list to inspect where work is
            intended to go next.
          </p>
          <p class="workflow-edge-editor__scope-note">
            For workflows using saved graph execution, these paths determine
            where work goes next. Save, validate, and publish changes before
            they can affect new work.
          </p>
        </div>
      </ng-template>
    </section>
  </div>
</section>
